import { useEffect, useState } from 'react';

const useConfirmValueUpdate = ({ value, onValueChange, onUpdatePayloadChange, onFocus, onBlur, onCommit }: any) => {
    // Basically value gets updated all the time, the update only gets propagated on commit
    // Here we intercept the commit and hold it until the user confirmed the change

    const [isFocused, setIsFocused] = useState<boolean>(false);
    const [unconfirmedValue, setUnconfirmedValue] = useState<string>(value);
    const [waitingForConfirm, setWaitingForConfirm] = useState<boolean>(false);
    const [pendingCommit, setPendingCommit] = useState<{ value: any; updatePayload?: any } | null>(null);

    useEffect(() => {
        // only allow external updates when the input is not focused
        if (!isFocused && !waitingForConfirm) {
            setUnconfirmedValue(value);
        }
    }, [value]);

    const handleFocus = (e) => {
        if (!waitingForConfirm) {
            setUnconfirmedValue(value);
        }
        setIsFocused(true);
        onFocus?.(e);
    };

    const handleBlur = (e) => {
        setIsFocused(false);
        if (unconfirmedValue !== value) {
            setWaitingForConfirm(true);
        } else {
            onBlur?.(e);
        }
    };

    const handleCommit = (committedValue: any, updatePayload?: any) => {
        if (committedValue !== unconfirmedValue) {
            // hold the commit until the user confirmed the change
            setPendingCommit({ value: committedValue, updatePayload });
            setWaitingForConfirm(true);
        } else {
            onCommit?.(committedValue, updatePayload);
        }
    };

    const handleConfirm = () => {
        setWaitingForConfirm(false);
        if (pendingCommit) {
            onCommit?.(pendingCommit.value, pendingCommit.updatePayload);
            setPendingCommit(null);
        }
        onBlur?.(null);
    };

    const handleCancel = () => {
        setWaitingForConfirm(false);
        setPendingCommit(null);

        // reset to the previous value
        onValueChange?.(unconfirmedValue);
        onUpdatePayloadChange?.({});

        onBlur?.(null);
    };

    return {
        onFocus: handleFocus,
        onBlur: handleBlur,
        onCommit: handleCommit,

        waitingForConfirm,
        onConfirm: handleConfirm,
        onCancel: handleCancel,
    };
};

export default useConfirmValueUpdate;
